import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Image as ImageIcon, X } from "lucide-react";
import TopBar from "@/components/TopBar";
import { useOutfitDraft } from "@/context/OutfitDraftContext";

const SUGGESTIONS = [
  "Timothée Chalamet",
  "Zendaya",
  "Rihanna",
  "Harry Styles",
  "Hailey Bieber",
  "A$AP Rocky",
  "Bella Hadid",
  "Tom Cruise",
];

function fileToResizedBase64(file, maxDim = 1024) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const s = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * s);
        height = Math.round(height * s);
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function CelebrityScreen() {
  const navigate = useNavigate();
  const { draft, update } = useOutfitDraft();
  const fileRef = useRef(null);
  const [error, setError] = useState("");

  const onPick = async (e) => {
    setError("");
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/image\/(jpeg|png|webp|jpg)/i.test(file.type)) {
      setError("Please pick a JPEG, PNG, or WEBP image.");
      return;
    }
    try {
      const b64 = await fileToResizedBase64(file, 1024);
      update({ imageBase64: b64, imageMime: "image/jpeg" });
    } catch {
      setError("Could not process image.");
    }
  };

  const canContinue = !!(draft.celebrity?.trim() || draft.imageBase64);

  return (
    <div className="screen fade-in" data-testid="celebrity-screen">
      <TopBar title="DRESS LIKE" />
      <div style={{ marginBottom: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>STYLE ICON</div>
        <h2 className="h1" style={{ fontSize: 34 }}>
          Dress like a <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>celebrity</em>
        </h2>
        <p className="body" style={{ marginTop: 12 }}>
          Type a name, or upload an inspiration photo.
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>
        <div className="field">
          <label className="label" htmlFor="celeb">Celebrity name</label>
          <input
            id="celeb"
            className="input"
            placeholder="e.g. Zendaya"
            value={draft.celebrity}
            onChange={(e) => update({ celebrity: e.target.value })}
            data-testid="celebrity-name-input"
          />
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="celebrity-suggestions">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              className={`chip ${draft.celebrity === s ? "active" : ""}`}
              onClick={() => update({ celebrity: s })}
              data-testid={`celebrity-suggestion-${s.replace(/\W/g, "-")}`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="divider-text">OR INSPIRATION PHOTO</div>

        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onPick}
          style={{ display: "none" }}
          data-testid="celebrity-file-input"
        />

        {draft.imageBase64 ? (
          <div style={{ position: "relative" }}>
            <img src={draft.imageBase64} alt="inspiration" className="preview-img" data-testid="celebrity-preview-img" />
            <button
              type="button"
              className="icon-btn"
              onClick={() => update({ imageBase64: null })}
              aria-label="Remove"
              data-testid="celebrity-clear-btn"
              style={{ position: "absolute", top: 10, right: 10, background: "rgba(0,0,0,0.6)" }}
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="tile"
            onClick={() => fileRef.current?.click()}
            data-testid="celebrity-upload-btn"
            style={{ alignItems: "center", justifyContent: "center", minHeight: 140 }}
          >
            <div className="tile-icon"><ImageIcon size={20} /></div>
            <div className="h3">Upload inspiration photo</div>
          </button>
        )}

        {error && <div className="error-text" data-testid="celebrity-error">{error}</div>}
      </div>

      <div style={{ marginTop: 16 }}>
        <button
          className="btn btn-primary"
          disabled={!canContinue}
          onClick={() => navigate("/style-pick")}
          data-testid="celebrity-continue-btn"
        >
          Continue
        </button>
      </div>
    </div>
  );
}
